import type { INonsoleReadinessService } from '../../services/nonsoleReadiness/INonsoleReadinessService';
import type { NonsoleReadinessReport } from '@playcastdotio/NonsoleReadinessTypes';
import { categorizeGates, type CategorizedGates } from './categorizeGates';
import { serviceContainer } from '../../services/ServiceContainer';

export type HostModeEntryPhase =
  | 'idle'
  | 'checking'
  | 'apps-running'
  | 'restart-setup'
  | 'please-wait'
  | 'contact-support'
  | 'several-issues'
  | 'restart-confirm';

const PLEASE_WAIT_POLL_MS = 15_000;

export interface HostModeEntryControllerDeps {
  nonsoleReadinessService: INonsoleReadinessService;
}

export class HostModeEntryController {
  private phase: HostModeEntryPhase = 'idle';
  private categorized: CategorizedGates | null = null;
  private error: string | null = null;
  private inFlight = false;
  private generation = 0;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private listeners: Array<() => void> = [];
  private destroyed = false;

  private readonly service: INonsoleReadinessService;
  private startedAt = 0;

  constructor(deps: HostModeEntryControllerDeps) {
    this.service = deps.nonsoleReadinessService;
  }

  getPhase(): HostModeEntryPhase { return this.phase; }
  getCategorized(): CategorizedGates | null { return this.categorized; }
  getError(): string | null { return this.error; }
  isInFlight(): boolean { return this.inFlight; }

  subscribe(listener: () => void): () => void {
    this.listeners.push(listener);
    return () => {
      const idx = this.listeners.indexOf(listener);
      if (idx > -1) this.listeners.splice(idx, 1);
    };
  }

  async start(): Promise<void> {
    if (this.inFlight || this.destroyed) return;

    this.generation++;
    const gen = this.generation;

    this.inFlight = true;
    this.phase = 'checking';
    this.error = null;
    this.startedAt = Date.now();
    serviceContainer.analyticsManager.sendHostModeActivationStartedEvent();
    this.notify();

    let report: NonsoleReadinessReport;
    try {
      report = await this.service.getFreshReport();
    } catch {
      if (this.destroyed || gen !== this.generation) return;
      this.inFlight = false;
      this.error = "Couldn't check readiness — try again";
      this.phase = 'idle';
      serviceContainer.analyticsManager.sendHostModeActivationFailedEvent({
        error: "Couldn't check readiness",
        phase: 'checking',
      });
      this.notify();
      return;
    }

    if (this.destroyed || gen !== this.generation) return;
    await this.route(report, gen);
  }

  async retry(): Promise<void> {
    if (this.inFlight || this.destroyed) return;
    await this.start();
  }

  async recheck(): Promise<void> {
    if (this.inFlight || this.destroyed) return;
    const gen = this.generation;

    this.inFlight = true;
    // Do NOT reset phase — keeps the current modal stable during poll
    this.notify();

    let report: NonsoleReadinessReport;
    try {
      report = await this.service.getFreshReport();
    } catch {
      if (this.destroyed || gen !== this.generation) return;
      this.inFlight = false;
      this.notify();
      return;
    }

    if (this.destroyed || gen !== this.generation) return;
    await this.route(report, gen);
  }

  confirmRestart(): void {
    if (this.destroyed || this.phase !== 'restart-setup') return;
    this.phase = 'restart-confirm';
    this.notify();
  }

  dismiss(): void {
    if (this.destroyed) return;
    this.generation++;
    this.clearPollTimer();
    this.inFlight = false;
    this.phase = 'idle';
    this.categorized = null;
    this.error = null;
    this.notify();
  }

  destroy(): void {
    this.destroyed = true;
    this.clearPollTimer();
    this.listeners = [];
  }

  private static readonly MAX_ROUTE_DEPTH = 3;

  private async route(report: NonsoleReadinessReport, gen: number, depth = 0): Promise<void> {
    if (depth >= HostModeEntryController.MAX_ROUTE_DEPTH) {
      this.inFlight = false;
      this.phase = 'contact-support';
      this.categorized = {
        appsHolding: [],
        userActions: [],
        pleaseWait: [],
        contactSupport: [{
          gateId: 'ENTRY_LOOP',
          category: 'contact-support',
          label: "Can't start Host Mode",
          detail: 'Repeated entry attempts failed. Please try again or contact support.',
        }],
        clearToEnter: false,
      };
      serviceContainer.analyticsManager.sendHostModeActivationFailedEvent({
        error: 'max_route_depth_exceeded',
        phase: 'entry-loop',
      });
      this.notify();
      return;
    }

    this.clearPollTimer();
    const cats = categorizeGates(report);
    this.categorized = cats;

    if (cats.clearToEnter) {
      try {
        const result = await this.service.requestEntry();
        if (this.destroyed || gen !== this.generation) return;

        if (result.accepted) {
          this.inFlight = false;
          this.phase = 'idle';
          this.categorized = null;
          serviceContainer.analyticsManager.sendHostModeActivationSucceededEvent({
            totalDurationMs: Date.now() - this.startedAt,
          });
          this.notify();
          return;
        }

        if (result.report) {
          await this.route(result.report, gen, depth + 1);
          return;
        }

        this.inFlight = false;
        this.phase = 'contact-support';
        this.categorized = {
          appsHolding: [],
          userActions: [],
          pleaseWait: [],
          contactSupport: [{
            gateId: 'UNKNOWN',
            category: 'contact-support',
            label: "Can't start Host Mode",
            detail: result.error ?? 'An unexpected error occurred.',
          }],
          clearToEnter: false,
        };
        this.notify();
        return;
      } catch {
        if (this.destroyed || gen !== this.generation) return;
        this.inFlight = false;
        this.error = "Couldn't check readiness — try again";
        this.phase = 'idle';
        this.categorized = null;
        serviceContainer.analyticsManager.sendHostModeActivationFailedEvent({
          error: "Couldn't check readiness",
          phase: 'entry-request',
        });
        this.notify();
        return;
      }
    }

    this.inFlight = false;
    this.phase = this.resolvePhase(cats);
    this.notify();

    if (this.phase !== 'idle') {
      const failingGates = [
        ...cats.appsHolding,
        ...cats.userActions.map(g => g.gateId),
        ...cats.pleaseWait.map(g => g.gateId),
        ...cats.contactSupport.map(g => g.gateId),
      ];
      serviceContainer.analyticsManager.sendHostModeActivationBlockedEvent({
        phase: this.phase,
        failingGates,
      });
    }

    if (this.phase === 'please-wait') {
      this.startPollTimer();
    }
  }

  private resolvePhase(cats: CategorizedGates): HostModeEntryPhase {
    const groups: Array<'appsHolding' | 'userActions' | 'pleaseWait' | 'contactSupport'> = [];
    if (cats.appsHolding.length > 0) groups.push('appsHolding');
    if (cats.userActions.length > 0) groups.push('userActions');
    if (cats.pleaseWait.length > 0) groups.push('pleaseWait');
    if (cats.contactSupport.length > 0) groups.push('contactSupport');

    if (groups.length === 1) {
      const single = groups[0];
      if (single === 'appsHolding') return 'apps-running';
      if (single === 'userActions') {
        if (cats.userActions.length === 1 && cats.userActions[0].actionKind === 'reboot') {
          return 'restart-setup';
        }
        return 'several-issues';
      }
      if (single === 'pleaseWait') return 'please-wait';
      if (single === 'contactSupport') return 'contact-support';
    }

    return 'several-issues';
  }

  private startPollTimer(): void {
    this.clearPollTimer();
    this.pollTimer = setInterval(() => {
      this.recheck();
    }, PLEASE_WAIT_POLL_MS);
  }

  private clearPollTimer(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
